import React from "react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import image1 from "../asset/Images/industrialdesign.jpeg";
import image2 from "../asset/Images/3dprinting.jpeg";
import image3 from "../asset/Images/caddesign.jpeg";
// import image4 from "../asset/Images/prototyping.jpeg";
// import image5 from "../asset/Images/productdesign.jpeg";
import image6 from "../asset/Images/pcbimage.jpg";
import image7 from "../asset/Images/12services/IoTIntegration.png";
// Each service now carries its own image, in the same order as the text list.
const servicesList = [
  {
    name: "Product Design",
    src: image1,
  },
  {
    name: "Prototyping",
    src: image2,
  },
  {
    name: "CAD Design",
    src: image3,
  },
  // {
  //   name: "3D Printing",
  //   src: image4,
  // },
  {
    name: "IoT Integration",
    src: image7,
  },
  {
    name: "PCB Design",
    src: image6,
  },
];

const stats = [
  { value: 4, suffix: "+", label: "Years in business" },
  { value: 100, suffix: "+", label: "Successfully completed projects" },
  { value: 90, suffix: "%", label: "Client returns" },
];

// How long each service stays highlighted before moving to the next one
// (matches the text entrance stagger, then keeps looping every 4s).
const STEP_MS = 4000;

function AnimatedCounter({ end, suffix, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated]);

  useEffect(() => {
    if (!hasAnimated) return;
    let start = 0;
    const increment = end / (duration / 20);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 20);
    return () => clearInterval(timer);
  }, [hasAnimated, end, duration]);

  return (
    <div ref={ref}>
      <h3 className="text-[clamp(36px,4vw,64px)] font-extrabold text-[#c021d6] leading-none">
        {count}{suffix}
      </h3>
    </div>
  );
}

function Homeser() {
  const [hoveredService, setHoveredService] = useState(0);
  const isHoveringRef = useRef(false);

  // Auto-cycle through the services in the same order they're written,
  // one every 4s, so the image "keeps pace" with the text as it appears.
  // Hovering a service pauses the cycle on that service; leaving it resumes
  // the loop from wherever it left off.
  useEffect(() => {
    const interval = setInterval(() => {
      if (isHoveringRef.current) return;
      setHoveredService((prev) => (prev + 1) % servicesList.length);
    }, STEP_MS);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="bg-white ">
      <div className="max-w-full mx-full px-4 sm:px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-28 py-16 sm:py-20 xl:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-10 lg:gap-16 xl:gap-24">

          {/* LEFT — service names */}
          <div className="flex flex-col items-center md:items-start">
            {servicesList.map((service, i) => (
              <motion.h2
                key={service.name}
                initial={{ opacity: 0, x: -80 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
                onMouseEnter={() => {
                  isHoveringRef.current = true;
                  setHoveredService(i);
                }}
                onMouseLeave={() => {
                  isHoveringRef.current = false;
                }}
                className={`text-[clamp(30px,4.5vw,68px)] leading-tight font-bold tracking-tight mb-1 sm:mb-2 cursor-default transition-all duration-300 hover:translate-x-2 ${
                  hoveredService === i
                    ? "text-[#b126c9] scale-105"
                    : "text-[#111111]"
                }`}
              >
                {service.name}
              </motion.h2>
            ))}
          </div>

          {/* RIGHT — single image that swaps to match the active service */}
          <div className="relative w-full h-[320px] sm:h-[380px] lg:h-[440px] rounded-2xl overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={servicesList[hoveredService].name}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="absolute inset-0 rounded-2xl overflow-hidden group cursor-pointer"
              >
                {/* Shimmer border on hover */}
                <motion.div
                  className="absolute inset-0 z-20 rounded-2xl pointer-events-none"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ boxShadow: "inset 0 0 0 2px #c021d6" }}
                />

                <img
                  src={servicesList[hoveredService].src}
                  alt={servicesList[hoveredService].name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 group-hover:brightness-110"
                />

                {/* Purple gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#c021d6]/15 to-[#7c3aed]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Bottom label slides up */}
                <motion.span
                  className="absolute bottom-4 left-4 text-white text-[14px] font-semibold drop-shadow z-10"
                  initial={{ opacity: 0, y: 8 }}
                  whileHover={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  {servicesList[hoveredService].name}
                </motion.span>

                {/* Top-right arrow icon appears on hover */}
                <motion.div
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center z-10"
                  initial={{ opacity: 0, scale: 0.6 }}
                  whileHover={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 16, color: "#fff" }} />
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

        </div>
      </div>

      {/* Stats */}
      <div className="bg-[#ffffff] py-10 sm:py-12 xl:py-16">
        <div className="max-w-[88rem] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-28 grid grid-cols-3 gap-4 sm:gap-8 text-center">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
            >
              <AnimatedCounter end={stat.value} suffix={stat.suffix} />
              <p className="text-[11px] sm:text-sm xl:text-base text-[#222] mt-2 sm:mt-3">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
export default Homeser;